import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { RoutesPaths } from "../../Router/constant";
import { EyeIcon, EyeOffIcon } from "./onboarding/icons";
import {
  MessageSquare,
  Users,
  BarChart3,
  CalendarDays,
  ArrowRight,
  Mail,
  Lock,
  Sparkles,
} from "lucide-react";
import { loginAction, googleLoginAction } from "../../actions/auth";

const FilledStarIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.5z" />
  </svg>
);

// What is waiting on the other side of the form. Sign-up lists reasons to
// start; this is the same shape of list, about coming back.
const WAITING = [
  {
    icon: MessageSquare,
    tone: "purple",
    title: "Your conversations",
    detail: "Everything your assistant handled",
  },
  {
    icon: Users,
    tone: "blue",
    title: "New leads",
    detail: "Captured while you were away",
  },
  {
    icon: BarChart3,
    tone: "green",
    title: "Live analytics",
    detail: "See what visitors are asking",
  },
];

export default function SignIn({
  onSuccess,
  // Optional: rendered on its own route, the link is a route change rather
  // than a panel swap.
  onSwitchToSignUp,
  notice = "",
  defaultEmail = "",
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const dispatch = useDispatch();

  const handleGoogleLogin = async () => {
    setError("");
    setGoogleLoading(true);
    const result = await dispatch(googleLoginAction());
    setGoogleLoading(false);
    if (result?.success) {
      onSuccess();
      return;
    }
    // A closed popup is a deliberate cancel, not something to warn about.
    if (!result?.cancelled && result?.message) {
      setError(result.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      setError("Please enter your email and password.");
      return;
    }
    if (!email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setError("");
    setSubmitting(true);

    const result = await dispatch(
      loginAction({ email: email.trim(), password }),
    );

    setSubmitting(false);

    if (result?.success) {
      onSuccess();
      return;
    }

    // The account exists but the address was never confirmed, and the API has
    // just mailed a fresh code — finish verifying rather than show an error.
    if (result?.needsVerification) {
      navigate(RoutesPaths.VERIFY_OTP, {
        state: {
          email: result.email || email.trim(),
          notice: result.message,
        },
      });
      return;
    }

    setError(result?.message || "Something went wrong. Please try again.");
  };

  return (
    <div className="auth-split">
      <div className="auth-split__brand auth-split__brand--light">
        <span className="auth-split__dots" aria-hidden="true" />

        <div className="auth-split__brand-inner">
          <div className="auth-brand-copy">
            <div className="auth-split__logoImg">
              <img src="/assets/logo.svg" alt="" />
            </div>

            {/* Broken after "Welcome back to" so the product name starts the
                second line, the way the sign-up headline does. */}
            <div className="auth-split__welcome auth-split__welcome--top">
              <h1>
                Welcome back to
                <br />
                <span>Hostor</span>
              </h1>
              <p>
                Sign in to pick up where you left off. Your AI receptionist has
                been answering visitors while you were away.
              </p>
            </div>

            <ul className="auth-feature-list">
              {WAITING.map(({ icon: Icon, tone, title, detail }) => (
                <li key={title}>
                  <span
                    className={`auth-feature-icon auth-feature-icon--${tone}`}
                  >
                    <Icon size={18} strokeWidth={2.2} />
                  </span>
                  <span className="auth-feature-text">
                    <strong>{title}</strong>
                    <span>{detail}</span>
                  </span>
                </li>
              ))}
            </ul>

            {/* What a customer says, rather than a count: this is the page
                someone comes back to, so it is worth being glad they did. */}
            <div className="auth-proof auth-proof--quote">
              <span className="auth-proof__stars" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <FilledStarIcon key={i} />
                ))}
              </span>
              <p className="auth-proof__quote">
                &ldquo;Hostor is like having a full-time receptionist that never
                sleeps. Our leads have increased by 40%!&rdquo;
              </p>
              <span className="auth-proof__person">
                <span className="auth-proof__avatar">SJ</span>
                <span className="auth-proof__who">
                  <strong>Sarah Johnson</strong>
                  <span>Founder, Growthly</span>
                </span>
              </span>
            </div>
          </div>

          <div className="auth-mascot">
            <div className="auth-mascot__stage">
              <img
                src="/assets/Robot-Mascot.png"
                alt=""
                className="img-fluid robotImage"
              />

              <div className="auth-mascot__bubble">
                <Sparkles size={16} strokeWidth={2.4} />
                <span>
                  Welcome back!
                  <strong>I&apos;ve been busy.</strong>
                </span>
              </div>

              {/* Decoration, so hidden rather than read out as stray icons. */}
              <span className="auth-mascot__tiles" aria-hidden="true">
                <span className="auth-mascot__tile auth-mascot__tile--chat">
                  <MessageSquare size={18} strokeWidth={2.2} />
                </span>
                <span className="auth-mascot__tile auth-mascot__tile--cal">
                  <CalendarDays size={18} strokeWidth={2.2} />
                </span>
                <span className="auth-mascot__tile auth-mascot__tile--chart">
                  <BarChart3 size={18} strokeWidth={2.2} />
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-split__form">
        <div className="auth-split__form-inner">
          <div className="auth-split__form-header">
            <h2>Sign in</h2>
            <p>
              Don&apos;t have an account?{" "}
              <button
                type="button"
                className="auth-inline-link"
                onClick={
                  onSwitchToSignUp || (() => navigate(RoutesPaths.SIGNUP))
                }
              >
                Sign up
              </button>
            </p>
          </div>

          {/* Google first, then the form - the same order as sign-up, so the
              two pages don't put the same two choices in opposite places. */}
          <div className="auth-social-stack">
            <button
              type="button"
              className="auth-social-btn auth-social-btn--wide"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
            >
              <img src="/assets/google-icon.svg" alt="" />
              {googleLoading ? "Signing in…" : "Sign in with Google"}
            </button>
          </div>

          <div className="auth-divider">
            <span>or continue with email</span>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {notice && !error && <p className="auth-success">{notice}</p>}
            {error && <p className="auth-error">{error}</p>}

            <label className="auth-field auth-field--iconed">
              <span>Work email</span>
              <span className="auth-field__icon" aria-hidden="true">
                <Mail size={18} strokeWidth={1.9} />
              </span>
              <input
                type="email"
                className="auth-plain-input"
                placeholder="Enter your work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>

            <label className="auth-field auth-field--iconed">
              <span>Password</span>
              <span className="auth-field__icon" aria-hidden="true">
                <Lock size={18} strokeWidth={1.9} />
              </span>
              <span className="auth-plain-input-wrap">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </span>
            </label>

            <div className="auth-row-between">
              <label className="auth-checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <Link
                to={RoutesPaths.FORGOT_PASSWORD}
                className="auth-forgot-link"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="onboarding__btn onboarding__btn--primary auth-submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                "Signing in..."
              ) : (
                <>
                  Sign in
                  <ArrowRight size={18} strokeWidth={2.4} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
